/** Ordered from rarest to most common — used for sorting and display. */
export const RARITY_ORDER = ['mythic', 'legendary', 'rare', 'common'] as const;

export type RarityName = (typeof RARITY_ORDER)[number];

/** Badge score for each rarity. */
export const rarityPoints = {
  mythic: 6,
  legendary: 4,
  rare: 2,
  common: 1,
} as const satisfies Record<RarityName, number>;

/** Priority modifier for each rarity. */
export const rarityDividers = {
  mythic: 2,
  legendary: 1,
  rare: 0.9,
  common: 0.5,
} as const satisfies Record<RarityName, number>;

/** Number of fake claimed assumptions for each rarity when deciding priority. */
export const rarityGhostProfiles = {
  mythic: 0,
  legendary: 1,
  rare: 2,
  common: 4,
} as const satisfies Record<RarityName, number>;

export type RarityPoints = (typeof rarityPoints)[RarityName];

/**
 * Deck limits: the most badges of this rarity *or rarer* one deck can hold.
 * e.g. `legendary: 5` means legendaries + mythics together can't exceed 5.
 */
export const DECK_LIMITS = {
  mythic: 1,
  legendary: 5,
  rare: 12,
  common: 12,
} as const satisfies Record<RarityName, number>;

/**
 * When true, custom decks may include unclaimed badges and Claimed Only is a toggle.
 * When false, custom decks are claimed-only, the toggle is hidden, leaderboards count
 * all wins (claimed custom decks and unmodified sample decks), and unmodified sample
 * decks are the only unclaimed exception.
 */
export const BATTLE_ALLOW_UNCLAIMED = false;

/** After the unique 5 are chosen by badgeScore, re-sort them rarest-tier first for display. */
export const sortBadgeHashByRarity = true;

/** Splash-stat color token. `light-common` is only a hover tint, not a badge rarity. */
export type StatScoreTone = RarityName | 'light-common';

/** Splash / non-effect catalog stat scores — each value uses this rarity’s color token. Effect pills use the card rarity. */
export const STAT_SCORE_RARITY = {
  0: 'light-common',
  1: 'light-common',
  2: 'common',
  3: 'common',
  4: 'rare',
  5: 'legendary',
} as const satisfies Record<0 | 1 | 2 | 3 | 4 | 5, StatScoreTone>;

export type StatScore = keyof typeof STAT_SCORE_RARITY;

const isStatScore = (score: number): score is StatScore => Number.isInteger(score) && score >= 0 && score <= 5;

/** Color rarity for a splash stat value. Out-of-range scores clamp to 0 or 5. */
export const rarityNameForStatScore = (score: number): StatScoreTone => {
  if (isStatScore(score)) return STAT_SCORE_RARITY[score];
  return score > 5 ? STAT_SCORE_RARITY[5] : STAT_SCORE_RARITY[0];
};

/** Effect and Show All: lines of effect text shown before hover. */
export const BATTLE_EFFECT_PREVIEW_LINES = 2;

/** Show each badge's stat bars even when the tile is not hovered. */
export const BATTLE_SHOW_STATS_AT_REST = true;

/** Deck stat totals: reaching this many points per deck slot earns the tier. */
export const DECK_STAT_TOTAL_PER_SLOT = {
  mythic: 2.25,
  legendary: 1,
} as const satisfies Partial<Record<RarityName, number>>;

/** Arena combo trio stat totals: reaching each total earns the tier. */
export const TRIO_STAT_TOTAL_AT = {
  mythic: 12,
  legendary: 6,
} as const satisfies Partial<Record<RarityName, number>>;

/** In-play stat totals (playable piles vs discard): deck thresholds are divided by this. */
export const IN_PLAY_DIVISION_MODIFIER = 1.5;

/** An in-play stat total of this or more is legendary, e.g. 12 slots × 1 / 1.5 = 8. */
export const statTotalLegendaryAt = (deckSize: number): number =>
  (deckSize * DECK_STAT_TOTAL_PER_SLOT.legendary) / IN_PLAY_DIVISION_MODIFIER;

/** Board Stats bars: a lead this large (or larger) is legendary instead of white. */
export const BOARD_STAT_LEGENDARY_LEAD = 6;

export type StatTotalTone = 'legendary' | 'lead' | 'zero';
export type BoardStatTone = 'legendary' | 'lead' | 'common';

/** In-play totals: legendary at or above `statTotalLegendaryAt`. Any smaller positive total is a lead. Zero stays unset. */
export const statTotalTone = (total: number, deckSize: number): StatTotalTone => {
  if (total >= statTotalLegendaryAt(deckSize)) return 'legendary';
  if (total > 0) return 'lead';
  return 'zero';
};

/** Losing or tied board stats stay common. A lead is white, or legendary at `BOARD_STAT_LEGENDARY_LEAD` or more. */
export const boardStatTone = (value: number, opponent: number): BoardStatTone => {
  if (value <= opponent) return 'common';
  if (value - opponent >= BOARD_STAT_LEGENDARY_LEAD) return 'legendary';
  return 'lead';
};

export type DeckStatTotalTone = 'mythic' | StatTotalTone;

export const deckStatTotalTone = (total: number, deckSize: number): DeckStatTotalTone => {
  if (total >= deckSize * DECK_STAT_TOTAL_PER_SLOT.mythic) return 'mythic';
  if (total >= deckSize * DECK_STAT_TOTAL_PER_SLOT.legendary) return 'legendary';
  if (total > 0) return 'lead';
  return 'zero';
};

export const trioStatTotalTone = (total: number): DeckStatTotalTone => {
  if (total >= TRIO_STAT_TOTAL_AT.mythic) return 'mythic';
  if (total >= TRIO_STAT_TOTAL_AT.legendary) return 'legendary';
  if (total > 0) return 'lead';
  return 'zero';
};

/**
 * Deck power (predicted win rate %): reaching each value earns that tier. Below `fair` still reads as `poor`.
 * Colors follow the CPU difficulties: poor is Impossible red, fair Hard yellow, good Easy green, great purple.
 */
export const DECK_POWER_TIER_AT = {
  poor: 25,
  fair: 40,
  good: 50,
  great: 75,
} as const;

export type DeckPowerTone = keyof typeof DECK_POWER_TIER_AT;

const DECK_POWER_TIERS_HIGH_FIRST: readonly DeckPowerTone[] = ['great', 'good', 'fair', 'poor'];

export const deckPowerTone = (percent: number): DeckPowerTone =>
  DECK_POWER_TIERS_HIGH_FIRST.find((tier) => percent >= DECK_POWER_TIER_AT[tier]) ?? 'poor';
